import React, { useState } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const GOLD = "#C9A84C";

export default function Login() {
  const { user, login, signup } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const sessionExpired = params.get("expired") === "1";
  const [mode, setMode] = useState("login"); // 'login' | 'signup'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user && user !== false) return <Navigate to="/" replace />;

  const afterAuth = (u) => {
    const returnTo = sessionStorage.getItem("fits4you_return_to");
    sessionStorage.removeItem("fits4you_return_to");
    const defaultRoute = u?.role === "athlete" ? "/minhas-inscricoes" : "/";
    nav(returnTo || defaultRoute, { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    let res;
    if (mode === "login") {
      res = await login(email, password);
    } else {
      res = await signup(name, email, password);
    }
    setBusy(false);
    if (!res.ok) setError(res.error);
    else afterAuth(res.user);
  };

  const switchMode = (m) => {
    setMode(m);
    setError("");
  };

  return (
    <div
      className="min-h-screen relative flex items-center justify-center overflow-hidden"
      data-testid="login-page"
    >
      {/* Elite Background */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: 'url("/stage-bg.jpg")',
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          filter: "brightness(0.55) contrast(1.05) saturate(1.05)",
        }}
        aria-hidden="true"
      />
      {/* Vignette + gold ambient */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.85) 100%)",
        }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-x-0 top-0 h-40 pointer-events-none"
        style={{
          background: "linear-gradient(180deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%)",
        }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-x-0 bottom-0 h-40 pointer-events-none"
        style={{
          background: "linear-gradient(0deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 100%)",
        }}
        aria-hidden="true"
      />

      <div className="relative z-10 w-full max-w-md px-6">
        {/* Brand */}
        <div className="flex flex-col items-center text-center mb-8">
          <img
            src="/logo.jpg"
            alt="FITS4YOU"
            className="w-24 h-24 rounded-sm object-contain bg-white p-2"
            style={{ boxShadow: `0 0 0 1px ${GOLD}, 0 8px 40px rgba(201,168,76,0.25)` }}
            data-testid="login-brand-logo"
          />
          <div
            className="mt-5 text-[10px] md:text-xs font-bold tracking-[0.55em]"
            style={{ color: GOLD }}
            data-testid="login-brand-tagline"
          >
            SISTEMA · FITS4YOU CHAMPIONSHIP SUITE
          </div>
          <div
            className="mt-1 h-px w-24"
            style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }}
          />
        </div>

        {/* Glass card */}
        <div
          className="relative rounded-sm overflow-hidden"
          style={{
            background: "rgba(10, 10, 12, 0.55)",
            border: "1px solid rgba(201,168,76,0.35)",
            backdropFilter: "blur(18px) saturate(140%)",
            WebkitBackdropFilter: "blur(18px) saturate(140%)",
            boxShadow: "0 24px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)",
          }}
          data-testid="login-card"
        >
          {/* Tabs */}
          <div className="flex" role="tablist" aria-label="Modo de acesso">
            <button
              type="button"
              role="tab"
              aria-selected={mode === "login"}
              data-testid="login-tab-login"
              onClick={() => switchMode("login")}
              className="flex-1 py-3 text-xs font-bold tracking-[0.35em] transition"
              style={{
                color: mode === "login" ? GOLD : "rgba(255,255,255,0.55)",
                borderBottom:
                  mode === "login" ? `2px solid ${GOLD}` : "2px solid transparent",
                background: mode === "login" ? "rgba(201,168,76,0.06)" : "transparent",
              }}
            >
              ENTRAR
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "signup"}
              data-testid="login-tab-signup"
              onClick={() => switchMode("signup")}
              className="flex-1 py-3 text-xs font-bold tracking-[0.35em] transition"
              style={{
                color: mode === "signup" ? GOLD : "rgba(255,255,255,0.55)",
                borderBottom:
                  mode === "signup" ? `2px solid ${GOLD}` : "2px solid transparent",
                background: mode === "signup" ? "rgba(201,168,76,0.06)" : "transparent",
              }}
            >
              CADASTRE-SE
            </button>
          </div>

          <div className="p-7 md:p-8">
            <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight leading-none">
              {mode === "login" ? "BEM-VINDO" : "CRIAR CONTA"}
            </h1>
            <p className="text-white/60 mt-2 mb-6 text-sm">
              {mode === "login"
                ? "Entre para acessar o FITS4YOU Championship Suite."
                : "Preencha seus dados para acessar como atleta."}
            </p>

            {sessionExpired && mode === "login" && (
              <div
                className="mb-4 text-sm text-[#FFCC00] border border-[#FFCC00]/40 bg-[#FFCC00]/10 px-3 py-2"
                data-testid="login-session-expired"
              >
                Sua sessão expirou por segurança. Faça login novamente — seus dados estão salvos.
              </div>
            )}

            <form onSubmit={submit} className="space-y-4" data-testid={`login-form-${mode}`}>
              {mode === "signup" && (
                <div>
                  <label className="overline block mb-2 text-white/70">Nome</label>
                  <input
                    data-testid="signup-name"
                    type="text"
                    required
                    autoFocus
                    minLength={2}
                    className="input-field bg-black/40 border-white/10 focus:border-[#C9A84C] focus:ring-0"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Seu nome completo"
                  />
                </div>
              )}
              <div>
                <label className="overline block mb-2 text-white/70">E-mail</label>
                <input
                  data-testid={mode === "signup" ? "signup-email" : "login-email"}
                  type="email"
                  required
                  autoFocus={mode === "login"}
                  className="input-field bg-black/40 border-white/10 focus:border-[#C9A84C] focus:ring-0"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="voce@dominio.com"
                />
              </div>
              <div>
                <label className="overline block mb-2 text-white/70">Senha</label>
                <input
                  data-testid={mode === "signup" ? "signup-password" : "login-password"}
                  type="password"
                  required
                  minLength={mode === "signup" ? 6 : undefined}
                  className="input-field bg-black/40 border-white/10 focus:border-[#C9A84C] focus:ring-0"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
                {mode === "signup" && (
                  <div className="mt-1 text-[11px] text-white/50">
                    Mínimo de 6 caracteres.
                  </div>
                )}
              </div>

              {error && (
                <div
                  data-testid="login-error"
                  className="text-sm text-[#C9A84C] border border-[#C9A84C]/30 bg-[#C9A84C]/10 px-3 py-2"
                >
                  {error}
                </div>
              )}

              <button
                data-testid={mode === "signup" ? "signup-submit" : "login-submit"}
                className="w-full py-3 font-display font-black tracking-[0.3em] text-sm transition disabled:opacity-40"
                style={{
                  background: GOLD,
                  color: "#0a0a0a",
                  boxShadow: "0 8px 24px rgba(201,168,76,0.25)",
                }}
                disabled={busy}
                type="submit"
              >
                {busy
                  ? mode === "signup"
                    ? "CRIANDO CONTA..."
                    : "ENTRANDO..."
                  : mode === "signup"
                  ? "CRIAR CONTA"
                  : "ENTRAR NO SISTEMA"}
              </button>
            </form>

            {/* Bottom toggle line */}
            <div className="text-center mt-5 text-sm text-white/60">
              {mode === "login" ? (
                <>
                  Não tem conta?{" "}
                  <button
                    type="button"
                    data-testid="login-switch-signup"
                    onClick={() => switchMode("signup")}
                    className="font-bold underline underline-offset-4"
                    style={{ color: GOLD }}
                  >
                    Cadastre-se
                  </button>
                </>
              ) : (
                <>
                  Já tem conta?{" "}
                  <button
                    type="button"
                    data-testid="signup-switch-login"
                    onClick={() => switchMode("login")}
                    className="font-bold underline underline-offset-4"
                    style={{ color: GOLD }}
                  >
                    Entrar
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="text-center mt-6">
          <a
            href="/publico"
            data-testid="public-results-link"
            className="text-white/50 hover:text-white text-xs uppercase tracking-[0.35em] underline underline-offset-4"
          >
            Ver resultados públicos
          </a>
        </div>
      </div>
    </div>
  );
}
